import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Pagination } from '../components/ui/pagination';
import { Loader } from '../components/ui/loader';
import {
  ArrowLeft,
  Tag,
  ShoppingBag,
  Package,
  TrendingUp,
  Calendar,
  ExternalLink,
  CheckCircle2,
  Upload,
  RefreshCw,
  XCircle,
  AlertCircle,
  ArrowRight,
  Search,
  Filter,
  ArrowUpDown,
  Database,
  Plus
} from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';

const ITEMS_PER_PAGE = 50;

const CompetitorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [competitor, setCompetitor] = useState(null);
  const [changeLogs, setChangeLogs] = useState([]);
  const [changeLogsCount, setChangeLogsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingChangeLogs, setLoadingChangeLogs] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'changes');

  // Separate loading states for tabs
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Tab data states
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);

  // Counts for pagination (from API)
  const [categoriesCount, setCategoriesCount] = useState(0);
  const [brandsCount, setBrandsCount] = useState(0);
  const [productsCount, setProductsCount] = useState(0);

  // Pagination states - initialize from URL params
  const [categoriesPage, setCategoriesPage] = useState(() => parseInt(searchParams.get('categoriesPage') || '1', 10));
  const [brandsPage, setBrandsPage] = useState(() => parseInt(searchParams.get('brandsPage') || '1', 10));
  const [productsPage, setProductsPage] = useState(() => parseInt(searchParams.get('productsPage') || '1', 10));
  const [changeLogsPage, setChangeLogsPage] = useState(() => parseInt(searchParams.get('changeLogsPage') || '1', 10));
  
  const isInitialMount = useRef(true);

  // Upload states
  const [uploading, setUploading] = useState(false);
  const [uploadFileType, setUploadFileType] = useState('products');
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [showDebug, setShowDebug] = useState(false);

  // Brand Management States
  const [brandMappings, setBrandMappings] = useState([]);
  const [webshopBrands, setWebshopBrands] = useState([]);
  const [brandStats, setBrandStats] = useState({ mapped: 0, ignored: 0, open: 0, total: 0 });
  const [syncing, setSyncing] = useState(false);
  const [autoMapping, setAutoMapping] = useState(false);
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [selectedBrands, setSelectedBrands] = useState(new Set());

  useEffect(() => {
    fetchCompetitorBasic();
    fetchBrandStats();
  }, [id]);

  // Fetch basic competitor info (without full nested data for faster loading)
  const fetchCompetitorBasic = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(`/api/tracking/competitors/${id}/?exclude_nested=true`);
      setCompetitor(response.data);
      // Update page title when competitor is loaded
      if (response.data) {
        document.title = `${response.data.name} - Mappy`;
        // Initialize counts from competitor data
        setCategoriesCount(response.data.categories_count || 0);
        setBrandsCount(response.data.brands_count || 0);
        setProductsCount(response.data.products_count || 0);
      }
    } catch (error) {
      console.error('Error fetching competitor:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to load competitor');
      document.title = 'Competitor Details - Mappy';
    } finally {
      setLoading(false);
    }
  };

  // Set initial page title
  useEffect(() => {
    document.title = 'Competitor Details - Mappy';
  }, []);

  // Sync state from URL params (handles initial load and URL changes)
  useEffect(() => {
    const urlTab = searchParams.get('tab') || 'changes';
    const urlCategoriesPage = parseInt(searchParams.get('categoriesPage') || '1', 10);
    const urlBrandsPage = parseInt(searchParams.get('brandsPage') || '1', 10);
    const urlProductsPage = parseInt(searchParams.get('productsPage') || '1', 10);
    const urlChangeLogsPage = parseInt(searchParams.get('changeLogsPage') || '1', 10);

    // Update tab if it changed in URL
    if (urlTab !== activeTab) {
      setActiveTab(urlTab);
    }

    // Update page states to match URL
    setCategoriesPage(urlCategoriesPage);
    setBrandsPage(urlBrandsPage);
    setProductsPage(urlProductsPage);
    setChangeLogsPage(urlChangeLogsPage);
  }, [searchParams]);

  // Update URL when state changes (user interactions)
  useEffect(() => {
    // Skip on initial mount - URL sync effect handles initial state
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const params = new URLSearchParams(searchParams);
    
    // Update tab param
    if (activeTab !== 'changes') {
      params.set('tab', activeTab);
    } else {
      params.delete('tab');
    }
    
    // Update page params based on current tab
    if (activeTab === 'categories') {
      if (categoriesPage > 1) {
        params.set('categoriesPage', categoriesPage.toString());
      } else {
        params.delete('categoriesPage');
      }
    } else if (activeTab === 'brands') {
      if (brandsPage > 1) {
        params.set('brandsPage', brandsPage.toString());
      } else {
        params.delete('brandsPage');
      }
    } else if (activeTab === 'products') {
      if (productsPage > 1) {
        params.set('productsPage', productsPage.toString());
      } else {
        params.delete('productsPage');
      }
    } else if (activeTab === 'changes') {
      if (changeLogsPage > 1) {
        params.set('changeLogsPage', changeLogsPage.toString());
      } else {
        params.delete('changeLogsPage');
      }
    }

    setSearchParams(params, { replace: true });
  }, [categoriesPage, brandsPage, productsPage, changeLogsPage, activeTab, searchParams, setSearchParams]);

  // Reset pagination to 1 when switching tabs and update URL
  useEffect(() => {
    // Skip on initial mount - URL sync effect handles initial state
    if (isInitialMount.current) {
      return;
    }

    const params = new URLSearchParams(searchParams);
    
    // Reset page to 1 for the new tab
    if (activeTab === 'categories') {
      setCategoriesPage(1);
      params.delete('categoriesPage');
    } else if (activeTab === 'brands') {
      setBrandsPage(1);
      params.delete('brandsPage');
    } else if (activeTab === 'products') {
      setProductsPage(1);
      params.delete('productsPage');
    } else if (activeTab === 'changes') {
      setChangeLogsPage(1);
      params.delete('changeLogsPage');
    }

    // Update tab param
    if (activeTab !== 'changes') {
      params.set('tab', activeTab);
    } else {
      params.delete('tab');
    }

    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fetch products when tab is active or page changes
  useEffect(() => {
    if (activeTab === 'products') {
      const page = parseInt(searchParams.get('productsPage') || '1', 10);
      fetchProducts(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id, searchParams]);

  // Fetch categories when tab is active or page changes
  useEffect(() => {
    if (activeTab === 'categories') {
      const page = parseInt(searchParams.get('categoriesPage') || '1', 10);
      fetchCategories(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id, searchParams]);

  // Fetch brands when tab is active or page changes
  useEffect(() => {
    if (activeTab === 'brands') {
      const page = parseInt(searchParams.get('brandsPage') || '1', 10);
      fetchBrands(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id, searchParams, brandFilter, brandSearchTerm]);

  // Fetch change logs when tab is active or page changes
  useEffect(() => {
    if (activeTab === 'changes') {
      const page = parseInt(searchParams.get('changeLogsPage') || '1', 10);
      fetchChangeLogs(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id, searchParams]);

  const fetchCategories = async (pageOverride = null) => {
    try {
      setLoadingCategories(true);
      const page = pageOverride !== null ? pageOverride : parseInt(searchParams.get('categoriesPage') || '1', 10);
      const limit = ITEMS_PER_PAGE;
      const offset = (page - 1) * ITEMS_PER_PAGE;
      const response = await axiosInstance.get(`/api/tracking/categories/?competitor=${id}&limit=${limit}&offset=${offset}`);
      // Handle paginated response
      if (response.data.results) {
        setCategories(response.data.results);
        setCategoriesCount(response.data.count || 0);
      } else {
        setCategories(response.data);
        setCategoriesCount(response.data.length);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchBrands = async (pageOverride = null) => {
    try {
      setLoadingBrands(true);
      const page = pageOverride !== null ? pageOverride : parseInt(searchParams.get('brandsPage') || '1', 10);
      const limit = ITEMS_PER_PAGE;
      const offset = (page - 1) * ITEMS_PER_PAGE;

      let url = `/api/tracking/brand-mappings/?competitor=${id}&limit=${limit}&offset=${offset}`;
      if (brandFilter !== 'all') {
        url += `&status=${brandFilter}`;
      }
      if (brandSearchTerm) {
        url += `&search=${brandSearchTerm}`;
      }

      // Fetch mappings and webshop brands
      const [mappingsResponse, webshopResponse] = await Promise.all([
        axiosInstance.get(url),
        webshopBrands.length === 0 ? axiosInstance.get('/api/tracking/webshop-brands/all/') : Promise.resolve({ data: webshopBrands })
      ]);

      if (webshopBrands.length === 0) {
        setWebshopBrands(webshopResponse.data.results || webshopResponse.data);
      }

      if (mappingsResponse.data.results) {
        setBrandMappings(mappingsResponse.data.results);
        setBrandsCount(mappingsResponse.data.count || 0);
      } else {
        setBrandMappings(mappingsResponse.data);
        setBrandsCount(mappingsResponse.data.length);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    } finally {
      setLoadingBrands(false);
    }
  };

  const fetchBrandStats = async () => {
    try {
      const response = await axiosInstance.get(`/api/tracking/brand-mappings/stats/?competitor=${id}`);
      setBrandStats(response.data);
    } catch (error) {
      console.error('Error fetching brand stats:', error);
    }
  };

  const updateBrandStats = () => {
    fetchBrandStats();
    // Also refresh basic info to ensure consistency
    fetchCompetitorBasic();
  };

  const getStatusBadge = (mapping) => {
    switch (mapping.status) {
      case 'mapped':
        if (mapping.webshop_brand_in_pim) {
          return <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" />In Catalog</Badge>;
        } else {
          return <Badge className="bg-orange-500"><CheckCircle2 className="h-3 w-3 mr-1" />Manual</Badge>;
        }
      case 'ignored':
        return <Badge variant="secondary"><XCircle className="h-3 w-3 mr-1" />Ignored</Badge>;
      case 'is_category':
        return <Badge className="bg-blue-500"><Tag className="h-3 w-3 mr-1" />Category</Badge>;
      case 'open':
        return <Badge variant="outline"><AlertCircle className="h-3 w-3 mr-1" />Open</Badge>;
      default:
        return <Badge>{mapping.status}</Badge>;
    }
  };

  const handleSyncFromPIM = async () => {
    try {
      setSyncing(true);
      const response = await axiosInstance.post('/api/tracking/webshop-brands/sync_from_pim/');

      if (response.data.success) {
        fetchBrands();
        updateBrandStats();
      }
    } catch (error) {
      console.error('Error syncing brands:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleAutoMap = async () => {
    try {
      setAutoMapping(true);
      // This endpoint maps all brands, we can filter for just this competitor if needed, 
      // but currently the endpoint maps everything. That's fine.
      const response = await axiosInstance.post('/api/tracking/brand-mappings/auto_map/');

      if (response.data.success) {
        fetchBrands();
        updateBrandStats();
      }
    } catch (error) {
      console.error('Error auto-mapping brands:', error);
    } finally {
      setAutoMapping(false);
    }
  };

  const handleUpdateMapping = async (mappingId, updates, removeFromList = false) => {
    try {
      // Optimistic update: remove from list immediately if requested
      if (removeFromList) {
        setBrandMappings(prev => prev.filter(m => m.id !== mappingId));
        setBrandsCount(prev => Math.max(0, prev - 1));
      }

      await axiosInstance.patch(`/api/tracking/brand-mappings/${mappingId}/`, updates);

      // Only do a full refresh if not doing optimistic update, or if we need fresh data
      if (!removeFromList) {
        fetchBrands();
      }
      updateBrandStats();
    } catch (error) {
      console.error('Error updating mapping:', error);
      // Revert optimistic update on error
      if (removeFromList) {
        fetchBrands();
      }
    }
  };

  const handleStatusChange = async (mappingId, newStatus) => {
    // Remove from current list if status change would hide it from current view
    const shouldRemove = brandFilter === 'all'
      ? (newStatus === 'ignored' || newStatus === 'is_category')
      : (newStatus !== brandFilter);
    await handleUpdateMapping(mappingId, { status: newStatus }, shouldRemove);
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedBrands.size === 0) return;

    // Optimistic update: remove all selected items
    const idsToRemove = Array.from(selectedBrands);
    setBrandMappings(prev => prev.filter(m => !selectedBrands.has(m.id)));
    setBrandsCount(prev => Math.max(0, prev - selectedBrands.size));
    setSelectedBrands(new Set());

    // Process all updates
    try {
      await Promise.all(
        idsToRemove.map(id =>
          axiosInstance.patch(`/api/tracking/brand-mappings/${id}/`, { status: newStatus })
        )
      );
      updateBrandStats();
    } catch (error) {
      console.error('Error in bulk update:', error);
      fetchBrands(); // Revert on error
    }
  };

  const toggleBrandSelection = (mappingId) => {
    setSelectedBrands(prev => {
      const newSet = new Set(prev);
      if (newSet.has(mappingId)) {
        newSet.delete(mappingId);
      } else {
        newSet.add(mappingId);
      }
      return newSet;
    });
  };

  const selectAllBrands = () => {
    const openBrands = brandMappings.filter(m => m.status === 'open').map(m => m.id);
    setSelectedBrands(new Set(openBrands));
  };

  const clearSelection = () => {
    setSelectedBrands(new Set());
  };

  const handleMapToBrand = async (mappingId, webshopBrandId) => {
    await handleUpdateMapping(mappingId, {
      status: 'mapped',
      webshop_brand: webshopBrandId
    });
  };

  const handleCreateBrand = async (mapping) => {
    const newName = window.prompt(`Create new Master Brand for "${mapping.competitor_brand_name}"?`, mapping.competitor_brand_name);

    if (newName && newName.trim()) {
      try {
        // 1. Create the new WebshopBrand
        const createResponse = await axiosInstance.post('/api/tracking/webshop-brands/', {
          name: newName.trim()
        });

        const newBrandId = createResponse.data.id;

        // 2. Add to webshopBrands list immediately
        setWebshopBrands(prev => [...prev, createResponse.data]);

        // 3. Map the current mapping to this new brand
        await handleMapToBrand(mapping.id, newBrandId);

      } catch (error) {
        console.error('Error creating brand:', error);
        alert('Failed to create new brand. It might already exist.');
      }
    }
  };

  const fetchProducts = async (pageOverride = null) => {
    try {
      setLoadingProducts(true);
      const page = pageOverride !== null ? pageOverride : parseInt(searchParams.get('productsPage') || '1', 10);
      const limit = ITEMS_PER_PAGE;
      const offset = (page - 1) * ITEMS_PER_PAGE;
      const response = await axiosInstance.get(`/api/tracking/products/?competitor=${id}&limit=${limit}&offset=${offset}`);
      // Handle paginated response
      if (response.data.results) {
        setProducts(response.data.results);
        setProductsCount(response.data.count || 0);
      } else {
        setProducts(response.data);
        setProductsCount(response.data.length);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchChangeLogs = async (page = 1) => {
    try {
      setLoadingChangeLogs(true);
      const limit = ITEMS_PER_PAGE;
      const offset = (page - 1) * ITEMS_PER_PAGE;
      const response = await axiosInstance.get(`/api/tracking/changelogs/?competitor=${id}&is_new=true&limit=${limit}&offset=${offset}`);
      const changeLogsData = response.data.results || response.data;
      const logsArray = Array.isArray(changeLogsData) ? changeLogsData : [];
      setChangeLogs(logsArray);
      if (response.data.count !== undefined) {
        setChangeLogsCount(response.data.count);
      } else {
        setChangeLogsCount(Array.isArray(changeLogsData) ? changeLogsData.length : 0);
      }
      // If current page is now out of range (e.g., after marking items read), go to previous page
      const count = response.data.count !== undefined ? response.data.count : logsArray.length;
      const totalPages = Math.ceil(count / ITEMS_PER_PAGE);
      if (page > 1 && totalPages > 0 && page > totalPages) {
        setChangeLogsPage(totalPages);
      }
    } catch (error) {
      console.error('Error fetching change logs:', error);
      setChangeLogs([]);
      setChangeLogsCount(0);
    } finally {
      setLoadingChangeLogs(false);
    }
  };

  const markChangeAsRead = async (changeLogId) => {
    try {
      await axiosInstance.post(`/api/tracking/changelogs/${changeLogId}/mark_read/`);
      fetchChangeLogs(changeLogsPage);
    } catch (error) {
      console.error('Error marking change as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axiosInstance.post('/api/tracking/changelogs/mark_all_read/', {
        competitor_id: parseInt(id)
      });
      setChangeLogs([]);
      setChangeLogsCount(0);
      setChangeLogsPage(1);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const toggleCategoryIgnore = async (categoryId) => {
    try {
      await axiosInstance.post(`/api/tracking/categories/${categoryId}/toggle_ignore/`);
      fetchCategories();
    } catch (error) {
      console.error('Error toggling category ignore:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', uploadFileType);

      const response = await axiosInstance.post(
        `/api/tracking/competitors/${id}/upload_xml/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setUploadResult({
        success: true,
        message: `Successfully processed ${uploadFileType}`,
        stats: response.data.stats,
      });

      // Refresh data after upload
      if (uploadFileType === 'products') {
        fetchProducts();
        setProductsPage(1);
      } else if (uploadFileType === 'categories') {
        fetchCategories();
        setCategoriesPage(1);
      } else if (uploadFileType === 'manufacturers') {
        // Refresh brands if manufacturers uploaded
        fetchBrands();
        setBrandsPage(1);
      }

      // Refresh competitor stats
      fetchCompetitorBasic();
      fetchChangeLogs();
    } catch (error) {
      setUploadResult({
        success: false,
        message: error.response?.data?.error || error.message || 'Upload failed',
      });
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const getChangeTypeColor = (changeType) => {
    if (changeType.includes('new')) return 'default';
    if (changeType.includes('removed')) return 'destructive';
    return 'secondary';
  };

  const getChangeTypeIcon = (changeType) => {
    if (changeType.includes('category')) return <Tag className="h-4 w-4" />;
    if (changeType.includes('brand')) return <ShoppingBag className="h-4 w-4" />;
    if (changeType.includes('product')) return <Package className="h-4 w-4" />;
    return <TrendingUp className="h-4 w-4" />;
  };

  // Calculate total pages from server-side counts
  const categoriesTotalPages = Math.ceil(categoriesCount / ITEMS_PER_PAGE);
  const brandsTotalPages = Math.ceil(brandsCount / ITEMS_PER_PAGE);
  const productsTotalPages = Math.ceil(productsCount / ITEMS_PER_PAGE);
  const changeLogsTotalPages = Math.ceil(changeLogsCount / ITEMS_PER_PAGE);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="flex flex-col items-center gap-4">
            <Loader size="lg" />
            <div className="text-muted-foreground">Loading competitor data...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !competitor) {
    return (
      <Layout>
        <div className="container mx-auto p-6">
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">
                {error || 'Competitor not found'}
              </p>
              <Button onClick={() => navigate('/competitors')} className="mt-4">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Competitors
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Use state counts (from API pagination) or fallback to competitor counts
  const displayCategoriesCount = categoriesCount || competitor?.categories_count || 0;
  const displayBrandsCount = brandsCount || competitor?.brands_count || 0;
  const displayProductsCount = productsCount || competitor?.products_count || 0;

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/competitors')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          {/* Competitor Logo */}
          {competitor.image_url ? (
            <img
              src={competitor.image_url}
              alt={competitor.name}
              className="h-10 w-10 rounded-lg object-contain bg-white border flex-shrink-0"
            />
          ) : (
            <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground text-sm font-medium flex-shrink-0">
              {competitor.name.substring(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{competitor.name}</h1>
            {competitor.website && (
              <a
                href={competitor.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1 mt-1"
              >
                {competitor.website}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>

        <Separator />

        {/* Upload section for Cloudflare-protected competitors */}
        {(competitor.name === 'Finnish Design Shop' || competitor.name === 'No-GA' || competitor.name === 'Made in Design') && (
          <Card>
            <CardHeader>
              <CardTitle>Upload XML Files</CardTitle>
              <CardDescription>
                Upload sitemap XML files (products, manufacturers, or categories)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {competitor.name === 'Finnish Design Shop' && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="text-sm font-medium text-blue-900 mb-2">Download sitemap files from:</div>
                    <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                      <li>
                        <a
                          href="https://www.finnishdesignshop.com/sitemaps/en-nl/products.xml.gz"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          Products: https://www.finnishdesignshop.com/sitemaps/en-nl/products.xml.gz
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://www.finnishdesignshop.com/sitemaps/en-nl/categories.xml.gz"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          Categories: https://www.finnishdesignshop.com/sitemaps/en-nl/categories.xml.gz
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://www.finnishdesignshop.com/sitemaps/en-nl/manufacturers.xml.gz"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          Manufacturers: https://www.finnishdesignshop.com/sitemaps/en-nl/manufacturers.xml.gz
                        </a>
                      </li>
                    </ul>
                  </div>
                )}
                {competitor.name === 'No-GA' && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="text-sm font-medium text-blue-900 mb-2">Download sitemap files from:</div>
                    <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                      <li>
                        <a
                          href="https://www.no-ga.com/api/sitemaps/prods/en/en-be"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          Products: https://www.no-ga.com/api/sitemaps/prods/en/en-be
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://www.no-ga.com/api/sitemaps/pages/en-be"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          Pages: https://www.no-ga.com/api/sitemaps/pages/en-be
                        </a>
                      </li>
                    </ul>
                  </div>
                )}
                {competitor.name === 'Made in Design' && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="text-sm font-medium text-blue-900 mb-2">Download sitemap files from:</div>
                    <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                      <li>
                        <a
                          href="https://www.madeindesign.com/sitemap/sitemap_fr_product.xml"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          Products: https://www.madeindesign.com/sitemap/sitemap_fr_product.xml
                        </a>
                        <span className="text-blue-600 ml-2">(Cloudflare protected - manual download required)</span>
                      </li>
                      <li>
                        <a
                          href="https://www.madeindesign.com/sitemap/sitemap_fr_manufacturer.xml"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          Manufacturers: https://www.madeindesign.com/sitemap/sitemap_fr_manufacturer.xml
                        </a>
                        <span className="text-blue-600 ml-2">(Cloudflare protected - manual download required)</span>
                      </li>
                    </ul>
                  </div>
                )}
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium">File Type:</label>
                  <select
                    value={uploadFileType}
                    onChange={(e) => setUploadFileType(e.target.value)}
                    className="px-3 py-2 border rounded-md"
                    disabled={uploading}
                  >
                    <option value="products">Products {competitor.name === 'Made in Design' ? '(sitemap_fr_product.xml)' : '(products.xml.gz)'}</option>
                    <option value="manufacturers">Manufacturers {competitor.name === 'Made in Design' ? '(sitemap_fr_manufacturer.xml)' : '(manufacturers.xml.gz)'}</option>
                    {competitor.name !== 'Made in Design' && (
                      <option value="categories">Categories (categories.xml.gz)</option>
                    )}
                  </select>
                </div>
                <div className="flex items-center gap-4">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".xml,.gz,.xml.gz"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="hidden"
                      id="xml-upload-input"
                    />
                    <Button
                      variant="outline"
                      disabled={uploading}
                      onClick={() => document.getElementById('xml-upload-input')?.click()}
                      type="button"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {uploading ? 'Uploading...' : 'Choose File'}
                    </Button>
                  </label>
                </div>
                {uploadResult && (
                  <div className={`p-4 rounded-md ${uploadResult.success
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                    }`}>
                    <div className="font-medium">{uploadResult.message}</div>
                    {uploadResult.stats && (
                      <div className="mt-2 text-sm space-y-2">
                        <div className="grid grid-cols-3 gap-2">
                          <div>Processed: <strong>{uploadResult.stats.processed || 0}</strong></div>
                          <div>Created: <strong>{uploadResult.stats.created || 0}</strong></div>
                          <div>Updated: <strong>{uploadResult.stats.updated || 0}</strong></div>
                        </div>
                        {(uploadResult.stats.debug || uploadResult.stats.errors) && (
                          <div className="mt-3">
                            <button
                              onClick={() => setShowDebug(!showDebug)}
                              className="text-sm font-medium text-blue-600 hover:text-blue-800 underline"
                            >
                              {showDebug ? '▼ Hide' : '▶ Show'} Debug Information
                            </button>
                            {showDebug && (
                              <div className="mt-2 p-3 bg-gray-50 rounded border border-gray-200">
                                <div className="text-xs space-y-1">
                                  <div><strong>File:</strong> {uploadResult.stats.debug.filename} ({uploadResult.stats.debug.file_size} bytes)</div>
                                  <div><strong>File Type:</strong> {uploadResult.stats.debug.file_type_detected}</div>
                                  {uploadResult.stats.debug.xml_structure && (
                                    <div>
                                      <strong>XML Structure:</strong>
                                      <ul className="ml-4 mt-1 list-disc">
                                        <li>Root tag: {uploadResult.stats.debug.xml_structure.root_tag}</li>
                                        <li>Children: {uploadResult.stats.debug.xml_structure.num_children}</li>
                                        {uploadResult.stats.debug.xml_structure.urls_found && (
                                          <>
                                            <li>Sitemap namespace URLs: {uploadResult.stats.debug.xml_structure.urls_found.sitemap_namespace || 0}</li>
                                            <li>Plain URLs: {uploadResult.stats.debug.xml_structure.urls_found.plain_urls || 0}</li>
                                            <li>Direct URLs: {uploadResult.stats.debug.xml_structure.urls_found.direct_urls || 0}</li>
                                            <li>Full namespace URLs: {uploadResult.stats.debug.xml_structure.urls_found.full_namespace || 0}</li>
                                            <li>Total URLs: {uploadResult.stats.debug.xml_structure.urls_found.total || 0}</li>
                                          </>
                                        )}
                                      </ul>
                                    </div>
                                  )}
                                  {uploadResult.stats.debug.parsing_details && uploadResult.stats.debug.parsing_details.length > 0 && (
                                    <div className="mt-2">
                                      <strong>Parsing Details:</strong>
                                      <pre className="mt-1 p-2 bg-white rounded text-xs overflow-auto max-h-60 border">
                                        {JSON.stringify(uploadResult.stats.debug.parsing_details, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {uploadResult.stats.errors && uploadResult.stats.errors.length > 0 && (
                          <div className="mt-2">
                            <div className="font-medium">Errors:</div>
                            <ul className="list-disc list-inside">
                              {uploadResult.stats.errors.slice(0, 10).map((error, idx) => (
                                <li key={idx} className="text-xs">{error}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-6 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Categories</CardDescription>
              <CardTitle className="text-3xl">{displayCategoriesCount}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Brands</CardDescription>
              <CardTitle className="text-3xl">{displayBrandsCount}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Products</CardDescription>
              <CardTitle className="text-3xl">{displayProductsCount}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        <div className="flex gap-2 border-b">
          <Button
            variant={activeTab === 'changes' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('changes')}
          >
            Recent Changes
            {changeLogsCount > 0 && (
              <Badge variant="destructive" className="ml-2">
                {changeLogsCount}
              </Badge>
            )}
          </Button>
          <Button
            variant={activeTab === 'categories' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('categories')}
          >
            Categories
            {displayCategoriesCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {displayCategoriesCount}
              </Badge>
            )}
          </Button>
          <Button
            variant={activeTab === 'brands' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('brands')}
            className="min-w-[120px]"
          >
            Brands
            {displayBrandsCount > 0 && (
              <Badge variant="secondary" className="ml-2 px-2" title={`${brandStats.ignored || 0} brands ignored`}>
                {brandStats.mapped || 0} / {brandStats.total || displayBrandsCount}
              </Badge>
            )}
          </Button>
          <Button
            variant={activeTab === 'products' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('products')}
          >
            Products
            {displayProductsCount > 0 && (
              <Badge variant="secondary" className="ml-2">
                {displayProductsCount}
              </Badge>
            )}
          </Button>
        </div>

        {activeTab === 'changes' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Recent Changes</CardTitle>
                  <CardDescription>
                    Track new categories, brands, and products
                  </CardDescription>
                </div>
                {changeLogsCount > 0 && (
                  <Button variant="outline" size="sm" onClick={markAllAsRead}>
                    Mark All Read
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loadingChangeLogs ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex flex-col items-center gap-4">
                    <Loader size="lg" />
                    <div className="text-muted-foreground">Loading changes...</div>
                  </div>
                </div>
              ) : changeLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No new changes detected
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="border rounded-md">
                    <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-muted/40 font-medium text-xs text-muted-foreground border-b uppercase tracking-wider">
                      <div className="col-span-2">Type</div>
                      <div className="col-span-6 md:col-span-7">Item</div>
                      <div className="col-span-3 md:col-span-2 text-right md:text-left">Date</div>
                      <div className="col-span-1 text-right">Action</div>
                    </div>
                    <div className="divide-y">
                      {changeLogs.map((log) => (
                        <div
                          key={log.id}
                          className="grid grid-cols-12 gap-4 px-4 py-2 items-center text-sm hover:bg-muted/5 transition-colors group"
                        >
                          <div className="col-span-2 flex items-center gap-2">
                            <Badge variant={getChangeTypeColor(log.change_type)} className="h-5 px-1.5 text-[10px] uppercase whitespace-nowrap">
                              {log.change_type.replace('new_', '').replace('_removed', '')}
                            </Badge>
                          </div>
                          <div className="col-span-6 md:col-span-7 truncate font-medium" title={log.item_name}>
                            {log.item_url ? (
                              <a
                                href={log.item_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:underline flex items-center gap-1 hover:text-primary"
                              >
                                {log.item_name}
                                <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </a>
                            ) : (
                              <span>{log.item_name}</span>
                            )}
                          </div>
                          <div className="col-span-3 md:col-span-2 text-xs text-muted-foreground text-right md:text-left">
                            {new Date(log.detected_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                          <div className="col-span-1 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 opacity-50 group-hover:opacity-100 hover:text-green-600 hover:bg-green-50"
                              onClick={() => markChangeAsRead(log.id)}
                              title="Mark as read"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="py-2">
                    <Pagination
                      currentPage={changeLogsPage}
                      totalPages={changeLogsTotalPages}
                      onPageChange={setChangeLogsPage}
                      pageSize={ITEMS_PER_PAGE}
                      totalItems={changeLogsCount}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'categories' && (
          <Card>
            <CardHeader>
              <CardTitle>Categories</CardTitle>
              <CardDescription>All categories tracked for {competitor.name}</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingCategories ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-4">
                    <Loader size="lg" />
                    <div className="text-muted-foreground">Loading categories...</div>
                  </div>
                </div>
              ) : displayCategoriesCount === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No categories tracked yet
                </div>
              ) : (
                <>
                  <div className="border rounded-md bg-card">
                    {/* Header */}
                    <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-muted/40 font-medium text-xs text-muted-foreground border-b uppercase tracking-wider">
                      <div className="col-span-5">Category</div>
                      <div className="col-span-3">First Seen</div>
                      <div className="col-span-2">Status</div>
                      <div className="col-span-2 text-right">Actions</div>
                    </div>

                    <div className="divide-y">
                      {categories.map((category) => (
                        <div
                          key={category.id}
                          className={`grid grid-cols-12 gap-4 px-4 py-2 items-center text-sm hover:bg-muted/5 transition-colors group ${category.is_ignored ? 'opacity-50' : ''}`}
                        >
                          {/* Category Name */}
                          <div className="col-span-5">
                            <div className="font-medium truncate" title={category.name}>
                              {category.url ? (
                                <a
                                  href={category.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:underline flex items-center gap-1 hover:text-primary"
                                >
                                  {category.name}
                                  <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </a>
                              ) : (
                                category.name
                              )}
                            </div>
                          </div>

                          {/* First Seen */}
                          <div className="col-span-3 text-muted-foreground text-xs">
                            {new Date(category.first_seen).toLocaleDateString()}
                          </div>

                          {/* Status */}
                          <div className="col-span-2">
                            {category.is_ignored ? (
                              <Badge variant="secondary" className="text-[10px] h-5">Ignored</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] h-5 text-green-600 border-green-200">Active</Badge>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="col-span-2 flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-7 px-2 text-xs opacity-50 group-hover:opacity-100 transition-opacity ${category.is_ignored ? 'text-green-600 hover:text-green-700 hover:bg-green-50' : 'text-muted-foreground hover:text-destructive'}`}
                              onClick={() => toggleCategoryIgnore(category.id)}
                              title={category.is_ignored ? 'Restore' : 'Ignore'}
                            >
                              {category.is_ignored ? (
                                <>
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Restore
                                </>
                              ) : (
                                <>
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Ignore
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Pagination
                    currentPage={categoriesPage}
                    totalPages={categoriesTotalPages}
                    onPageChange={setCategoriesPage}
                    pageSize={ITEMS_PER_PAGE}
                    totalItems={categoriesCount}
                  />
                </>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'brands' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Brands</CardTitle>
                  <CardDescription>
                    Manage brand mappings for {competitor.name}
                    {brandsCount > 0 && ` (${brandsCount} brands)`}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleSyncFromPIM}
                    disabled={syncing}
                    variant="outline"
                    size="sm"
                  >
                    <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Syncing...' : 'Sync PIM'}
                  </Button>
                  <Button
                    onClick={handleAutoMap}
                    disabled={autoMapping}
                    size="sm"
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    {autoMapping ? 'Auto-Mapping...' : 'Auto-Map'}
                  </Button>
                </div>
              </div>

              <div className="flex gap-4 items-center mt-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search brands..."
                    className="w-full pl-10 pr-4 py-2 border rounded-md"
                    value={brandSearchTerm}
                    onChange={(e) => setBrandSearchTerm(e.target.value)}
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  {['all', 'open', 'mapped', 'ignored', 'is_category'].map(status => (
                    <Button
                      key={status}
                      variant={brandFilter === status ? 'default' : 'outline'}
                      onClick={() => setBrandFilter(status)}
                      size="sm"
                      className="capitalize"
                    >
                      {status === 'mapped' ? 'In Catalog' : status === 'is_category' ? 'Categories' : status}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingBrands ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-4">
                    <Loader size="lg" />
                    <div className="text-muted-foreground">Loading brands...</div>
                  </div>
                </div>
              ) : brandMappings.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <ShoppingBag className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    {displayBrandsCount > 0 ? 'Brands Detected' : 'No Brands Found'}
                  </h3>
                  <p className="text-muted-foreground text-center mb-4 max-w-sm">
                    {displayBrandsCount > 0
                      ? `We found ${displayBrandsCount} brands in the scraped data that haven't been imported into the management system yet.`
                      : 'No brands have been detected for this competitor yet. Upload a sitemap or wait for the scraper.'}
                  </p>
                  {displayBrandsCount > 0 && (
                    <Button onClick={handleAutoMap} size="lg">
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Initialize & Auto-Map {displayBrandsCount} Brands
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {/* Bulk Action Bar */}
                  {selectedBrands.size > 0 && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-blue-800">
                        <CheckCircle2 className="h-4 w-4" />
                        <span className="font-medium">{selectedBrands.size} selected</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs bg-blue-100 border-blue-300 text-blue-700 hover:bg-blue-200"
                          onClick={() => handleBulkStatusChange('is_category')}
                        >
                          <Tag className="h-3 w-3 mr-1" />
                          Mark as Categories
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
                          onClick={() => handleBulkStatusChange('ignored')}
                        >
                          <XCircle className="h-3 w-3 mr-1" />
                          Ignore All
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={clearSelection}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="border rounded-md bg-card">
                    {/* Header */}
                    <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-muted/40 font-medium text-xs text-muted-foreground border-b uppercase tracking-wider">
                      <div className="col-span-1 flex items-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                          checked={selectedBrands.size > 0 && selectedBrands.size === brandMappings.filter(m => m.status === 'open').length}
                          onChange={(e) => e.target.checked ? selectAllBrands() : clearSelection()}
                          title="Select all open brands"
                        />
                      </div>
                      <div className="col-span-3 lg:col-span-3">Competitor Brand</div>
                      <div className="col-span-2 lg:col-span-2">Status</div>
                      <div className="col-span-4 lg:col-span-4">Master Brand (Map To)</div>
                      <div className="col-span-2 lg:col-span-2 text-right">Actions</div>
                    </div>

                    <div className="divide-y">
                      {brandMappings.map((mapping) => (
                        <div key={mapping.id} className={`grid grid-cols-12 gap-4 px-4 py-2 items-center text-sm hover:bg-muted/5 transition-colors group ${selectedBrands.has(mapping.id) ? 'bg-blue-50/50' : ''}`}>
                          {/* Checkbox */}
                          <div className="col-span-1">
                            {mapping.status === 'open' && (
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                                checked={selectedBrands.has(mapping.id)}
                                onChange={() => toggleBrandSelection(mapping.id)}
                              />
                            )}
                          </div>
                          {/* Competitor Brand */}
                          <div className="col-span-3 lg:col-span-3">
                            <div className="font-medium truncate" title={mapping.competitor_brand_name}>
                              {mapping.competitor_brand_url ? (
                                <a
                                  href={mapping.competitor_brand_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:underline flex items-center gap-1 hover:text-primary"
                                >
                                  {mapping.competitor_brand_name}
                                  <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </a>
                              ) : (
                                mapping.competitor_brand_name
                              )}
                            </div>
                            {mapping.keyword_research_summary && (
                              <div className="flex items-center text-xs text-blue-600 mt-1" title={`Last researched: ${new Date(mapping.keyword_research_summary.last_research_date).toLocaleDateString()}`}>
                                <Database className="h-3 w-3 mr-1" />
                                <span>{mapping.keyword_research_summary.count} keywords</span>
                              </div>
                            )}
                          </div>

                          {/* Status */}
                          <div className="col-span-2 lg:col-span-1">
                            {getStatusBadge(mapping)}
                          </div>

                          {/* Master Brand */}
                          <div className="col-span-4 lg:col-span-4">
                            {mapping.status === 'mapped' && mapping.webshop_brand_name ? (
                              <div className="flex items-center gap-2">
                                <a
                                  href={`https://www.google.com/search?q=${encodeURIComponent(mapping.webshop_brand_name)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`font-medium truncate hover:underline ${mapping.webshop_brand_in_pim ? 'text-blue-600' : 'text-orange-600'}`}
                                >
                                  {mapping.webshop_brand_name}
                                </a>
                                {mapping.webshop_brand_in_pim ? (
                                  <Badge variant="secondary" className="text-[10px] h-5 px-1 bg-blue-50 text-blue-700 border-blue-200 whitespace-nowrap">
                                    In Catalog
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary" className="text-[10px] h-5 px-1 bg-orange-50 text-orange-700 border-orange-200 whitespace-nowrap">
                                    Manual
                                  </Badge>
                                )}
                              </div>
                            ) : mapping.status !== 'mapped' ? (
                              <div className="flex items-center gap-2">
                                <select
                                  className="h-8 w-full max-w-[200px] border rounded-md px-2 text-sm bg-background"
                                  defaultValue=""
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      handleMapToBrand(mapping.id, parseInt(e.target.value));
                                    }
                                  }}
                                >
                                  <option value="" disabled>Map to...</option>
                                  {webshopBrands.map((brand) => (
                                    <option key={brand.id} value={brand.id}>
                                      {brand.name}
                                    </option>
                                  ))}
                                </select>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleCreateBrand(mapping)}
                                  title="Create new Master Brand"
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : null}
                          </div>

                          {/* Actions */}
                          <div className="col-span-2 lg:col-span-2 flex justify-end gap-2 opacity-50 group-hover:opacity-100 transition-opacity">
                            {mapping.status === 'open' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 px-2 text-xs text-muted-foreground hover:text-blue-600 hover:bg-blue-50"
                                  onClick={() => handleStatusChange(mapping.id, 'is_category')}
                                  title="This is actually a category, not a brand"
                                >
                                  <Tag className="h-3 w-3 mr-1" />
                                  Category
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 text-muted-foreground hover:text-destructive p-0"
                                  onClick={() => handleStatusChange(mapping.id, 'ignored')}
                                  title="Ignore"
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </>
                            )}

                            {(mapping.status === 'mapped' || mapping.status === 'ignored' || mapping.status === 'is_category') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 text-muted-foreground"
                                onClick={() => handleStatusChange(mapping.id, 'open')}
                                title={mapping.status === 'mapped' ? 'Unmap' : 'Reopen'}
                              >
                                {mapping.status === 'mapped' ? 'Unmap' : 'Reopen'}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Pagination
                    currentPage={brandsPage}
                    totalPages={brandsTotalPages}
                    onPageChange={setBrandsPage}
                    pageSize={ITEMS_PER_PAGE}
                    totalItems={brandsCount}
                  />
                </>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'products' && (
          <Card>
            <CardHeader>
              <CardTitle>Products</CardTitle>
              <CardDescription>All products tracked for {competitor.name}</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingProducts ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-4">
                    <Loader size="lg" />
                    <div className="text-muted-foreground">Loading products...</div>
                  </div>
                </div>
              ) : displayProductsCount === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No products tracked yet
                </div>
              ) : (
                <>
                  <div className="border rounded-md bg-card">
                    {/* Header */}
                    <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-muted/40 font-medium text-xs text-muted-foreground border-b uppercase tracking-wider">
                      <div className="col-span-5">Product</div>
                      <div className="col-span-2">Brand</div>
                      <div className="col-span-2">Category</div>
                      <div className="col-span-2">Price</div>
                      <div className="col-span-1 text-right">Link</div>
                    </div>

                    <div className="divide-y">
                      {products.map((product) => (
                        <div
                          key={product.id}
                          className="grid grid-cols-12 gap-4 px-4 py-2 items-center text-sm hover:bg-muted/5 transition-colors group"
                        >
                          {/* Product Name */}
                          <div className="col-span-5">
                            <div className="font-medium truncate" title={product.name}>
                              {product.url ? (
                                <a
                                  href={product.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:underline flex items-center gap-1 hover:text-primary"
                                >
                                  <span className="truncate">{product.name}</span>
                                  <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </a>
                              ) : (
                                product.name
                              )}
                            </div>
                          </div>

                          {/* Brand */}
                          <div className="col-span-2 text-muted-foreground text-xs truncate" title={product.brand_name}>
                            {product.brand_name || '-'}
                          </div>

                          {/* Category */}
                          <div className="col-span-2 text-muted-foreground text-xs truncate" title={product.category_name}>
                            {product.category_name || '-'}
                          </div>

                          {/* Price */}
                          <div className="col-span-2 text-xs font-medium">
                            {product.price ? (
                              <span className="text-green-600">€{parseFloat(product.price).toFixed(2)}</span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </div>

                          {/* Link */}
                          <div className="col-span-1 flex justify-end">
                            {product.url && (
                              <a
                                href={product.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground opacity-50 group-hover:opacity-100 transition-opacity"
                              >
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Pagination
                    currentPage={productsPage}
                    totalPages={productsTotalPages}
                    onPageChange={setProductsPage}
                    pageSize={ITEMS_PER_PAGE}
                    totalItems={productsCount}
                  />
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default CompetitorDetail;
